import { Divider, Box, Grid, Hidden } from "@mui/material"
import Icon from "@mdi/react"
import { mdiCheck } from "@mdi/js"

import useToggle from "@secureo/common/hooks/useToggle"

import Button from "components/Buttons/Button"
import Price, { TaxString } from "components/Price"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"

import useTranslation from "hooks/useTranslation"

import { makeStyles } from "makeStyles"
import { netToGross } from "@secureo/common/utils/tax/taxUtils"
import changeImageSize from "utils/changeImageSize"

import { CountryCode } from "@secureo/common/typings/CountryCode"

import { ProductConfigurationsBySku } from "@secureo/common/reducers/productDetailPageReducer"
import {
	ConfigurationGroup,
	ConfigurationGroupProductsBySku,
} from "@secureo/common/typings/Product"

interface DescriptionProps {
	description: string
}

const Description = ({ description }: DescriptionProps) => {
	const { t } = useTranslation("product")
	const [showDescription, toggleShowDescription] = useToggle(true)

	return (
		<>
			<Button
				color="link"
				onClick={toggleShowDescription}
				style={{
					cursor: "pointer",
					textDecoration: "underline",
				}}
			>
				<div>{t(showDescription ? "showDescription" : "hideDescription")}</div>
			</Button>
			{!showDescription && (
				<p
					dangerouslySetInnerHTML={{
						__html: description,
					}}
				/>
			)}
		</>
	)
}

interface AddToConfigurationsSingleSelectProps {
	isLoading: boolean
	isSelected: boolean
	onSelect: () => void
}

const AddToConfigurationsSingleSelect = ({
	isLoading,
	isSelected,
	onSelect,
}: AddToConfigurationsSingleSelectProps) => {
	const { t } = useTranslation("product")
	return (
		<div style={{ width: "100px" }}>
			{!isSelected ? (
				<Button
					disabled={isLoading}
					onClick={onSelect}
					style={{ background: "none", border: "1px solid #7abfac", color: "#7abfac" }}
				>
					<span>{t("select")}</span>
				</Button>
			) : (
				<Button disabled={isLoading} color="primary" onClick={onSelect}>
					<Icon path={mdiCheck} size={1} color="#f7fafa" />
				</Button>
			)}
		</div>
	)
}

interface AddToConfigurationsMultiSelectProps {
	isLoading: boolean
	sku: string
	maxQuantity: number
	quantity: number
	setConfigurationQuantity: (sku: string, quantity: number) => void
}

const AddToConfigurationsMultiSelect = ({
	isLoading,
	sku,
	maxQuantity,
	quantity,
	setConfigurationQuantity,
}: AddToConfigurationsMultiSelectProps) => {
	const { t } = useTranslation("product")

	if (maxQuantity === 1)
		return (
			<div style={{ width: "100px" }}>
				{quantity === 0 ? (
					<Button
						disabled={isLoading}
						onClick={() => setConfigurationQuantity(sku, 1)}
						style={{
							background: "none",
							border: "1px solid #7abfac",
							color: "#7abfac",
						}}
					>
						<span>{t("select")}</span>
					</Button>
				) : (
					<Button color="primary" onClick={() => setConfigurationQuantity(sku, 0)}>
						<Icon path={mdiCheck} size={1} color="#f7fafa" />
					</Button>
				)}
			</div>
		)

	return (
		<div style={{ minWidth: "60px" }}>
			<Box mr={1}>
				<ChangeAmountButton
					quantity={quantity}
					onChange={(quantity: number) => setConfigurationQuantity(sku, quantity)}
					disabled={isLoading}
					maxQuantity={maxQuantity}
					minQuantity={0}
				/>
			</Box>
		</div>
	)
}

const useStyles = makeStyles()(() => ({
	gridItem: {
		flexBasis: "50%",
		position: "relative",
		padding: "10px",
		boxSizing: "border-box",
	},
	gridItemContent: {
		display: "flex",
	},
	"@media only screen and (max-width: 1333px)": {
		gridItem: {
			flexBasis: "50%",
		},
	},
	"@media only screen and (max-width: 815px)": {
		gridItem: {
			flexBasis: "100%",
		},
	},
}))

const ConfigurationGroupSingleOrMulti = ({
	isLoading,
	countryCode,
	taxRate,
	configurationGroup,
	configurationGroupProductsBySku,
	selectedConfigurationsBySku,
	onSingleConfigurationGroupConfigurationSet,
	onMultiConfigurationGroupConfigurationSet,
}: Props) => {
	const { classes } = useStyles()
	const { name: configurationGroupName, description, type, items } = configurationGroup

	return (
		<>
			<Box mb={2} p={1} style={{ background: "#F7FAFA", borderRadius: "3px" }}>
				<Box ml={1}>
					<h2 style={{ margin: 0 }}>{configurationGroupName}</h2>
					<p
						dangerouslySetInnerHTML={{
							__html: description,
						}}
					/>
				</Box>
				<Divider />
				<div>
					{items.map((configurationGroupItem, i) => {
						const { sku, maxQuantity } = configurationGroupItem
						const configurationProduct = configurationGroupProductsBySku?.[sku]

						const netPriceCents =
							configurationProduct?.pricesByCountryCode?.[countryCode] ?? 0
						const grossPriceCents = Math.round(netToGross(netPriceCents, taxRate))

						if (!configurationProduct) return null
						const { name, description, image } = configurationProduct

						const showMultiSelect = ["multi", "select-multi"].includes(type) // TODO: only on "multi"?
						const quantity = selectedConfigurationsBySku?.[sku]?.quantity ?? 0

						const setConfigurationQuantity = (sku: string, quantity: number) => {
							onMultiConfigurationGroupConfigurationSet(
								configurationGroupProductsBySku,
								configurationGroupName,
								sku,
								quantity,
							)
						}

						const isLastElement = i === items.length - 1

						return (
							<div className={classes.gridItem} key={sku}>
								<div className={classes.gridItemContent}>
									{image && (
										<div>
											<img
												alt={name}
												style={{
													maxWidth: "60px",
													marginRight: "15px",
													padding: "3px",
													border: "1px solid lightgrey",
												}}
												src={changeImageSize(image, "small")}
											/>
										</div>
									)}

									<div style={{ width: "100%" }}>
										<Grid container spacing={3}>
											<Grid item xs={12} md={8}>
												<strong>{name}</strong>
												<br />
												{description && (
													<Description description={description} />
												)}
											</Grid>
											<Grid item xs={12} md={4}>
												<div style={{ textAlign: "right" }}>
													<span style={{ marginRight: "10px" }}>
														<strong>
															<Price
																priceInEuroCents={grossPriceCents}
															/>
														</strong>
													</span>
													<span className="small">
														<TaxString />
													</span>
												</div>
												<Box
													mt={2}
													style={{
														display: "flex",
														justifyContent: "flex-end",
														width: "100%",
														padding: 0,
													}}
												>
													{showMultiSelect ? (
														<AddToConfigurationsMultiSelect
															isLoading={isLoading}
															sku={sku}
															maxQuantity={maxQuantity}
															quantity={quantity}
															setConfigurationQuantity={
																setConfigurationQuantity
															}
														/>
													) : (
														<AddToConfigurationsSingleSelect
															isLoading={isLoading}
															isSelected={quantity > 0}
															onSelect={() => {
																onSingleConfigurationGroupConfigurationSet(
																	configurationGroupProductsBySku,
																	configurationGroupName,
																	sku,
																)
															}}
														/>
													)}
												</Box>
											</Grid>
										</Grid>
										{!isLastElement && (
											<Hidden smUp>
												<Box mt={2}>
													<Divider />
												</Box>
											</Hidden>
										)}
									</div>
								</div>
							</div>
						)
					})}
				</div>
			</Box>
		</>
	)
}

interface Props {
	isLoading: boolean
	countryCode: CountryCode
	taxRate: number
	configurationGroup: ConfigurationGroup
	configurationGroupProductsBySku: ConfigurationGroupProductsBySku
	selectedConfigurationsBySku: ProductConfigurationsBySku
	onSingleConfigurationGroupConfigurationSet: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
	) => void
	onMultiConfigurationGroupConfigurationSet: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
		quantity: number,
	) => void
}

export default ConfigurationGroupSingleOrMulti
